import type { BotLaunchStatus, BotReplyMode } from '../../types/leadBot.js';
import { BOT_LAUNCH_STATUSES } from '../../types/leadBot.js';

// SMS Lead Bot: LAUNCH STATUS. PURE. (Club onboarding, 2026-11-04.)
//
// ⚠️⚠️ THIS ANSWERS "MAY THIS BOT SPEAK TO REAL LEADS AT ALL", AND NOTHING ELSE. It is deliberately not `mode`
// (supervised or not) and deliberately not the allowlist (which numbers). The three are combined in exactly one
// place for a send, `resolveAutonomy` (mode.ts), and this file supplies the launch half of that decision plus the
// rules for MOVING between statuses. A second place that combines them is how a "setting up" club texts a lead.
//
//   status       real leads                         test numbers (allowlist)
//   setting_up   nothing at all                      answered
//   testing      nothing at all                      answered
//   live         per replyMode (approve | auto)      as configured
//   on_hold      nothing; inbound is recorded        nothing from the bot (a person may still reply by hand)
//
// ⚠️ AN ABSENT `launchStatus` IS A LEGACY DOC, READ AS LIVE + APPROVE (or ON HOLD when `isActive` is false), so
// deploy and the migration can happen in either order. `explicit` tells the callers which case they are in: inbound
// routing changes only for a bot that has been given a status on purpose.

export type LaunchCapable = {
  launchStatus?: BotLaunchStatus | null;
  replyMode?: BotReplyMode | null;
  isActive?: boolean | null;
};

export interface ResolvedLaunch {
  status: BotLaunchStatus;
  replyMode: BotReplyMode;
  /** True when the doc carries a valid status. False = a legacy doc read through the default. */
  explicit: boolean;
}

export function resolveLaunch(config: LaunchCapable): ResolvedLaunch {
  const stored = config.launchStatus;
  const explicit = typeof stored === 'string' && (BOT_LAUNCH_STATUSES as string[]).includes(stored);
  const status: BotLaunchStatus = explicit ? (stored as BotLaunchStatus) : config.isActive === false ? 'on_hold' : 'live';
  const replyMode: BotReplyMode = config.replyMode === 'auto' ? 'auto' : 'approve';
  return { status, replyMode, explicit };
}

export const LAUNCH_LABELS: Record<BotLaunchStatus, string> = {
  setting_up: 'Setting up',
  testing: 'Testing',
  live: 'Live',
  on_hold: 'Paused',
};

export const LAUNCH_HELP: Record<BotLaunchStatus, string> = {
  setting_up: 'Setting up: nothing is sent to real leads yet.',
  testing: 'Testing: only your test numbers get texts. Nothing is sent to real leads.',
  live: 'Live: real leads are texted.',
  on_hold: 'Paused: the assistant sends nothing. New texts are recorded and your team is told.',
};

export const REPLY_MODE_LABELS: Record<BotReplyMode, string> = {
  approve: 'Approve each reply',
  auto: 'The assistant replies on its own',
};

export type LaunchRefusalCode = 'not_launched' | 'on_hold';
export interface LaunchRefusal {
  code: LaunchRefusalCode;
  detail: string;
}

/**
 * Whether a message from the BOT (a draft being approved, a follow-up, an opener) may go to this number, looking
 * only at the launch status. ⚠️ `phoneIsTest` is "this number is on the bot's effective allowlist": the only numbers
 * a club that is not live may text. Null = clear.
 */
export function launchSendRefusal(config: LaunchCapable, phoneIsTest: boolean): LaunchRefusal | null {
  const { status } = resolveLaunch(config);
  if (status === 'on_hold') {
    return { code: 'on_hold', detail: 'This club’s assistant is paused, so it sends nothing. A person can still reply by hand.' };
  }
  if ((status === 'setting_up' || status === 'testing') && !phoneIsTest) {
    return {
      code: 'not_launched',
      detail: `${status === 'setting_up' ? 'Setting up' : 'Testing'}: nothing is sent to real leads. Only the test numbers get texts until the club goes live.`,
    };
  }
  return null;
}

/** True when real-lead automation (Speed to Lead intake, pool replies) is permitted for this bot at all. */
export function isLiveForRealLeads(config: LaunchCapable): boolean {
  return resolveLaunch(config).status === 'live';
}

/**
 * A short reason a club's lead intake must not start Sam for this lead, or null. ⚠️ Used by intake and the pool: a
 * `setting_up` / `testing` club answers a TEST number only (a test lead from the Speed to Lead test form), an
 * `on_hold` club answers nothing, a `live` club is as configured.
 */
export function launchIntakeRefusal(config: LaunchCapable, phoneIsTest: boolean): LaunchRefusal | null {
  return launchSendRefusal(config, phoneIsTest);
}

// ---------------------------------------------------------------------------
// Moving between statuses
// ---------------------------------------------------------------------------

export type LaunchActor = 'kyck_admin' | 'kyck_staff' | 'client';

/**
 * Who may make which move. Kyck admins: any. Everyone else (Kyck staff who are not admins, and the club itself):
 * testing <-> live, live <-> paused, and back to testing. ⚠️ Nobody but an admin moves a club back to "setting up",
 * and a club cannot jump from setting up straight to live: it goes through testing, where the go-live gate is read.
 */
export function canTransition(from: BotLaunchStatus, to: BotLaunchStatus, actor: LaunchActor): boolean {
  if (from === to) return false;
  if (actor === 'kyck_admin') return true;
  if (to === 'setting_up') return false;
  if (from === 'setting_up') return to === 'testing';
  if (to === 'testing') return true;
  if (from === 'testing') return to === 'live';
  if (from === 'live') return to === 'on_hold';
  if (from === 'on_hold') return to === 'live';
  return false;
}

export interface GoLiveInput {
  /** Items the existing `computeSetup` go-live checklist still lists as not done. */
  checklistOutstanding: readonly string[];
  /** Human labels of every copied section, promotion or follow-up still marked "needs review". */
  needsReview: readonly string[];
}

export interface GoLiveRefusal {
  detail: string;
  checklistOutstanding: string[];
  needsReview: string[];
}

/**
 * ⚠️ THE GO-LIVE GATE (testing or setting up -> live). Refused unless the setup checklist is all done AND no copied
 * section still needs review. The refusal NAMES exactly what is missing, so nobody is left to guess. Resuming from
 * "paused" is not a go-live: the club has been live before, so only the status check applies there.
 */
export function goLiveRefusal(from: BotLaunchStatus, input: GoLiveInput): GoLiveRefusal | null {
  if (from === 'on_hold') return null;
  if (input.checklistOutstanding.length === 0 && input.needsReview.length === 0) return null;
  const parts: string[] = [];
  if (input.checklistOutstanding.length > 0) parts.push(`Setup is not finished: ${input.checklistOutstanding.join('; ')}.`);
  if (input.needsReview.length > 0) parts.push(`Check before going live: ${input.needsReview.join('; ')}.`);
  return {
    detail: parts.join(' '),
    checklistOutstanding: [...input.checklistOutstanding],
    needsReview: [...input.needsReview],
  };
}
